import { useState, useEffect } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useCreatePhaseMutation, useUpdatePhaseMutation, useGetCategoriesQuery } from '../../../Services/PathwayAdminAPI';
import '../../../Styles/RoleManagement.css';
import '../../../Styles/PhaseForm.css';

const PhaseForm = ({ show, onHide, phase = null, onSuccess, preselectedCategoryId = null }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    description: '',
    phase_number: 1,
    order_index: 0,
  });
  const [errors, setErrors] = useState({});

  const { data: categoriesData } = useGetCategoriesQuery();
  const [createPhase, { isLoading: isCreating }] = useCreatePhaseMutation();
  const [updatePhase, { isLoading: isUpdating }] = useUpdatePhaseMutation();

  const isEditMode = !!phase;
  const isLoading = isCreating || isUpdating;
  const categories = categoriesData?.data || [];

  useEffect(() => {
    if (!show) {
      setFormData({
        category_id: '',
        name: '',
        description: '',
        phase_number: 1,
        order_index: 0,
      });
      setErrors({});
      return;
    }

    if (phase) {
      setFormData({
        category_id: phase.category_id || '',
        name: phase.name || '',
        description: phase.description || '',
        phase_number: phase.phase_number || 1,
        order_index: phase.order_index || 0,
      });
    } else {
      setFormData({
        category_id: preselectedCategoryId || '',
        name: '',
        description: '',
        phase_number: 1,
        order_index: 0,
      });
    }
    setErrors({});
  }, [phase, show, preselectedCategoryId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Phase name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Phase name must not exceed 255 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must not exceed 1000 characters';
    }

    if (!formData.phase_number || formData.phase_number < 1) {
      newErrors.phase_number = 'Phase number must be at least 1';
    }

    if (formData.order_index < 0) {
      newErrors.order_index = 'Order index must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' || name === 'phase_number' || name === 'category_id' 
        ? parseInt(value) || (name === 'category_id' ? '' : 0) 
        : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await updatePhase({
          id: phase.id,
          ...formData,
        }).unwrap();
      } else {
        response = await createPhase(formData).unwrap();
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.message || `Phase ${isEditMode ? 'updated' : 'created'} successfully`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#FD9C00',
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
      onHide();
    } catch (error) {
      console.error('Error saving phase:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} phase`,
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Body>
        <h5>{isEditMode ? 'Edit Phase' : 'Add New Phase'}</h5>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Category <span className="red-star">*</span>
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className={errors.category_id ? 'is-invalid' : ''}
                  disabled={isEditMode}
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <p className="error-text">{errors.category_id}</p>}
                {isEditMode && (
                  <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Category cannot be changed after creation
                  </small>
                )}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Phase Name <span className="red-star">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter phase name (e.g., PHASE 1: CONCEPT & FEASIBILITY)"
                  className={errors.name ? 'is-invalid' : ''}
                  maxLength={255}
                />
                {errors.name && <p className="error-text">{errors.name}</p>}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter phase description (optional)"
                  rows={4}
                  className={errors.description ? 'is-invalid' : ''}
                  maxLength={1000}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #CFD6E1',
                    fontFamily: 'Open Sans',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
                {errors.description && <p className="error-text">{errors.description}</p>}
              </div>
            </Col>

            <Col xs={12} sm={6} md={6} lg={6}>
              <div className="form-control">
                <label>
                  Phase Number <span className="red-star">*</span>
                </label>
                <input
                  type="number"
                  name="phase_number"
                  value={formData.phase_number}
                  onChange={handleChange}
                  placeholder="Enter phase number"
                  min={1}
                  className={errors.phase_number ? 'is-invalid' : ''}
                />
                {errors.phase_number && <p className="error-text">{errors.phase_number}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Phase number for display (e.g., 1, 2, 3)
                </small>
              </div>
            </Col>

            <Col xs={12} sm={6} md={6} lg={6}>
              <div className="form-control">
                <label>
                  Order Index <span className="red-star">*</span>
                </label>
                <input
                  type="number"
                  name="order_index"
                  value={formData.order_index}
                  onChange={handleChange}
                  placeholder="Enter order index"
                  min={0}
                  className={errors.order_index ? 'is-invalid' : ''}
                />
                {errors.order_index && <p className="error-text">{errors.order_index}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Lower numbers appear first
                </small>
              </div>
            </Col>
          </Row>
          <Row>
            <div className="btn-action">
              <button
                type="button"
                className="btn-outline-black"
                onClick={onHide}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={isLoading ? 'btn-primary-disabled' : 'btn-primary'}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default PhaseForm;
